import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import pdfjsWorker from "react-pdf/dist/esm/pdf.worker.entry";
import CustomButton from "./Button";
import moment from "moment";
import SignatureModal from "./SignatureModal";
import { serverBaseUrl, frontendCollabUrl } from "../config/envs";
import axios from "axios";
import { showErrorToast } from "../utils/toast";
import { FETCH_SIGNER_CHART_DATA } from "../redux/actions/actionTypes";
import { useDispatch } from "react-redux";
import { PDFDocument, rgb } from "pdf-lib";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// pdfjs shares one worker between react-pdf's <Document> and our text-coord
// extraction; when the <Document> remounts (keyed on currentIndex) it can tear
// the worker down while a task is still pending, which pdf.js INTERNALS reject
// with a benign "Worker was terminated" — no app-level promise to catch. The
// suppression handler for that event lives in public/index.html: it must
// register before webpack-dev-server's overlay client (which is prepended to
// the bundle entry), and window listeners fire in registration order, so any
// handler added from bundle code — even at module scope — runs after the
// overlay's and can no longer stop it.

// NOVA-148: the exact attestation statement, maintained as part of Qualiphy's
// compliance records. Do not reword without a compliance sign-off.
const ATTESTATION_TEXT =
  "I attest that I have personally reviewed all assigned charts and that any " +
  "necessary recommendations have been documented. I understand this " +
  "attestation will be maintained as part of Qualiphy's compliance records.";

export default function CollabChart({
  data,
  searchText = "Treatment Status",
  onModalClick,
  onClose,
  open,
  limit,
  page,
  setLimit,
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [numPages, setNumPages] = useState(null);
  const [error, setError] = useState("");
  const [filteredCoordinates, setFilteredCoordinates] = useState([]);
  const [textContent, setTextContent] = useState([]);
  const [signImage, setSignImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  // NOVA-148 packet info (provider, review month, attestation state) — the
  // charts live in the collab backend; the sign backend proxies them.
  const [packet, setPacket] = useState(null);
  const [attestChecked, setAttestChecked] = useState(false);
  const [attesting, setAttesting] = useState(false);
  // NOVA-146 chart notes: per-document local edits keyed by sign_link_id.
  // { text, savedText, savedAt, saving, error } — savedText is what the
  // server has, so dirtiness is a simple comparison and a refetch never
  // clobbers an unsaved edit.
  const [notes, setNotes] = useState({});
  const notesRef = useRef(notes);
  notesRef.current = notes;
  // Sort a COPY (sort() mutates in place — this used to reorder the Redux-held
  // data.data itself), then treat sortedPdf as the ONLY row order in this
  // component: currentIndex indexes it, currentDoc is the row being signed,
  // and pdfFiles is built from it. Never index data.data positionally —
  // sortedPdf and data.data disagree about order, and a positional read off
  // data.data would upload against the wrong row's sign_secret_id.
  const sortedPdf = useMemo(
    () => [...(data?.data ?? [])].sort((a, b) => b.status - a.status),
    [data?.data]
  );
  const currentDoc = sortedPdf?.[currentIndex];
  const signerInfo = currentDoc?.signer;
  const signedDate = new Date(signerInfo?.date_signed);
  const { chartId } = useParams();
  const formattedSignedDate =
    "Reviewed on: " + moment(signedDate).format("MM/DD/YYYY");
  const dispatch = useDispatch();
  const frameRef = useRef(null);
  // Set when the first Sign+Next click has to detour through the adopt-a-
  // signature modal: once the signature lands, signing resumes automatically
  // so adopting still counts as the single click the flow promises.
  const pendingSignAdvance = useRef(false);
  const allFinalized = data?.statusCounts?.totalUncompleted === 0;
  const getChartId = parseInt(chartId?.split("-")?.[0]);
  const totalResults = data?.pagination?.totalResults || 0;

  // NOVA-129: packet progress for the reviewer's progress bar. reviewedCount is
  // the server-side count of completed documents (status 2), so it reflects
  // real persisted state, not the in-page currentIndex — the bar stays correct
  // across refetches, Previous/Next, and re-entry. Clamped so a stale count can
  // never render a >100% bar.
  const reviewedCount = Math.min(
    data?.statusCounts?.totalCompleted ?? 0,
    totalResults
  );
  const progressPct =
    totalResults > 0 ? Math.round((reviewedCount / totalResults) * 100) : 0;

  // Previous/Next browse the whole review session, so every row must be in the
  // fetched page (signed rows sort to the END of the backend query and drop
  // out of a partial page entirely). PDFs are no longer bulk-preloaded — see
  // the blob cache below — so a full page is just row metadata, not downloads.
  useEffect(() => {
    if (totalResults > limit) setLimit(totalResults);
  }, [totalResults, limit, setLimit]);

  // NOVA-138: after each refetch (i.e. after each signing), pin the view to
  // the first UNSIGNED row. Manual Previous/Next navigation between refetches
  // is untouched — this only runs when the data identity changes.
  useEffect(() => {
    if (allFinalized || !sortedPdf?.length) return;
    const idx = sortedPdf.findIndex((doc) => doc.status !== 2);
    if (idx >= 0) setCurrentIndex(idx);
  }, [allFinalized, sortedPdf]);

  const currentSigned = currentDoc?.status === 2;
  const isLastChart = totalResults > 0 && currentIndex === totalResults - 1;
  // NOVA-180: attributed packets (requires_attestation = 1) don't sign each
  // chart — documents are marked REVIEWED, and the single signature belongs
  // to the packet attestation. Strictly === 1 so legacy packets AND older
  // backends (field missing) keep the signing flow.
  const reviewOnly = Number(packet?.requires_attestation) === 1;

  // Signed rows (and finalized sessions) show the stamped artifact; unsigned
  // rows show the original merged chart PDF.
  const pdfFiles = useMemo(
    () =>
      sortedPdf?.map((doc) =>
        doc.status === 2 && doc.final_document_url
          ? doc.final_document_url
          : doc.created_s3_url
      ) || [],
    [sortedPdf]
  );

  // Lazily fetch the current chart's PDF (plus its neighbours for snappy
  // Previous/Next) into object URLs, cached by source URL. The old approach
  // preloaded EVERY fetched PDF up front, which stops scaling once the page
  // holds the whole session (a high-volume provider is 100+ documents).
  const blobCacheRef = useRef(new Map());
  const [, setBlobVersion] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const wanted = [currentIndex, currentIndex + 1, currentIndex - 1]
      .filter((i) => i >= 0 && i < pdfFiles.length)
      .map((i) => pdfFiles[i])
      .filter((url) => url && !blobCacheRef.current.has(url));
    if (!wanted.length) return undefined;

    Promise.all(
      wanted.map(async (url) => {
        try {
          const response = await fetch(url, { mode: "cors" });
          if (!response.ok)
            throw new Error(`Failed to fetch PDF: ${response.status}`);
          const blobUrl = URL.createObjectURL(await response.blob());
          if (!cancelled) blobCacheRef.current.set(url, blobUrl);
        } catch (err) {
          if (!cancelled) setError(err.message);
        }
      })
    ).then(() => {
      if (!cancelled) setBlobVersion((v) => v + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [pdfFiles, currentIndex]);

  const currentPdf = blobCacheRef.current.get(pdfFiles[currentIndex]) || null;
  const pdfReady = !!currentPdf;

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  // The PDF lives in its own scrollable frame; reset that frame (not the
  // window) whenever the viewed chart changes.
  useEffect(() => {
    frameRef.current?.scrollTo({ top: 0, behavior: "instant" });
  }, [currentIndex]);

  // NOVA-148 compliance audit: record every OPEN of a chart document — each
  // time a document becomes the visible chart (initial load, Next/Previous,
  // auto-advance), including re-opens of already-signed charts. The guard only
  // deduplicates re-renders of the SAME visible document; returning to a
  // document logs a fresh event. Fire-and-forget: a failed log never blocks
  // the review.
  const lastLoggedLinkRef = useRef(null);
  useEffect(() => {
    const row = sortedPdf?.[currentIndex];
    if (!row?.id) return;
    if (lastLoggedLinkRef.current === row.id) return;
    lastLoggedLinkRef.current = row.id;
    axios
      .post(
        `${serverBaseUrl}/log_chart_view`,
        { sign_link_id: row.id },
        { headers: { "sign-secret-id": chartId } }
      )
      .catch(() => {});
  }, [currentIndex, sortedPdf, chartId]);

  // ── NOVA-146 chart notes ───────────────────────────────────────────────────
  // Hydrate from the fetched rows (the charts list carries note /
  // note_created_at / note_updated_at). Only rows WITHOUT local unsaved edits
  // are (re)hydrated, so a refetch mid-typing never clobbers the textarea.
  useEffect(() => {
    if (!sortedPdf?.length) return;
    setNotes((prev) => {
      const next = { ...prev };
      for (const row of sortedPdf) {
        if (!row?.id) continue;
        const existing = next[row.id];
        const server = row.note || "";
        if (!existing || existing.text === existing.savedText) {
          next[row.id] = {
            text: server,
            savedText: server,
            savedAt: row.note_updated_at || null,
            saving: false,
            error: null,
          };
        }
      }
      return next;
    });
  }, [sortedPdf]);

  // Save one document's note (upsert; empty clears). keepalive lets the same
  // call run from pagehide, so a closed tab still flushes the last edit.
  const saveNote = useCallback(
    async (linkId, { keepalive = false } = {}) => {
      const entry = notesRef.current[linkId];
      if (!entry || entry.text === entry.savedText) return;
      const text = entry.text;
      setNotes((p) => ({ ...p, [linkId]: { ...p[linkId], saving: true, error: null } }));
      try {
        const response = await fetch(`${serverBaseUrl}/chart_note`, {
          method: "POST",
          keepalive,
          headers: {
            "Content-Type": "application/json",
            "sign-secret-id": chartId,
          },
          body: JSON.stringify({ sign_link_id: linkId, note: text }),
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body?.message || "save failed");
        setNotes((p) => ({
          ...p,
          [linkId]: {
            ...p[linkId],
            savedText: text,
            savedAt: body?.note?.updated_at || new Date().toISOString(),
            saving: false,
            error: null,
          },
        }));
      } catch (err) {
        setNotes((p) => ({
          ...p,
          [linkId]: { ...p[linkId], saving: false, error: err.message || "save failed" },
        }));
      }
    },
    [chartId]
  );

  // Debounced autosave: 1.5s after the last keystroke on the visible chart.
  const currentLinkId = sortedPdf?.[currentIndex]?.id;
  const currentNote = (currentLinkId && notes[currentLinkId]) || null;
  useEffect(() => {
    if (!currentLinkId || !currentNote) return undefined;
    if (currentNote.text === currentNote.savedText) return undefined;
    const t = setTimeout(() => saveNote(currentLinkId), 1500);
    return () => clearTimeout(t);
  }, [currentLinkId, currentNote?.text, currentNote?.savedText, saveNote]);

  // Definite flush on chart moves: when the visible document changes, save the
  // one we just left (Next/Previous/auto-advance all funnel through here).
  const prevLinkRef = useRef(null);
  useEffect(() => {
    const prev = prevLinkRef.current;
    prevLinkRef.current = currentLinkId;
    if (prev && prev !== currentLinkId) saveNote(prev);
  }, [currentLinkId, saveNote]);

  // Best-effort flush when the tab is hidden or closed (keepalive survives
  // page teardown) — covers "left the page open / walked away / closed it".
  useEffect(() => {
    const flush = () => {
      for (const [linkId, entry] of Object.entries(notesRef.current)) {
        if (entry.text !== entry.savedText) saveNote(Number(linkId), { keepalive: true });
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [saveNote]);

  // Return to the portal's chart list. The portal remembers the month the
  // review was opened from (sessionStorage, per tab) and restores that drill-
  // down on arrival, so this lands on the SAME month's Chart List — not the
  // folder grid. /app rather than the portal root, which bounces through the
  // login page before its auth check settles. Unsaved notes are flushed first
  // (the pagehide keepalive flush is a backstop, not the primary path).
  const returnToChartList = useCallback(async () => {
    try {
      const dirty = Object.entries(notesRef.current).filter(
        ([, e]) => e.text !== e.savedText
      );
      await Promise.all(dirty.map(([linkId]) => saveNote(Number(linkId))));
    } catch (_) {
      /* saveNote records per-note errors; leaving must not be blocked */
    }
    if (frontendCollabUrl) {
      // Pass the packet's review month as a FALLBACK for the portal's
      // remembered drill-down: a reviewer opened in a fresh tab (deep link,
      // reminder email) has no sessionStorage on the portal origin, and
      // without this Back would land on the folder grid instead of the month.
      const base = `${frontendCollabUrl.replace(/\/+$/, "")}/app`;
      window.location.href = packet?.review_month
        ? `${base}?month=${encodeURIComponent(packet.review_month)}`
        : base;
    } else {
      window.location.reload();
    }
  }, [saveNote, packet?.review_month]);

  // (Defined above uploadGeneratedPdf: NOVA-176 finishes exempt packets from
  // that callback, and a const referenced in its deps array must already be
  // initialized when the deps evaluate during render.)
  const uploadGeneratedPdf = useCallback(
    async (file) => {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("file", file);
      try {
        const uploadResponse = await axios.post(
          `${serverBaseUrl}/upload-signed-chart-review`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              "sign-secret-id": currentDoc.sign_secret_id,
            },
          }
        );
        if (uploadResponse.data?.data?.url) {
          await axios.post(
            `${serverBaseUrl}/submit_collab_final_document`,
            {
              sign_link_id: currentDoc.id,
              final_document_url: uploadResponse.data?.data?.url,
              chart_id: getChartId,
              signer_id: signerInfo?.id,
              date_signed: new Date(),
              sign_secret_id: data?.data?.[currentIndex]?.sign_secret_id,
            },
            {
              headers: {
                "sign-secret-id": currentDoc.sign_secret_id,
              },
            }
          );
          dispatch({
            type: FETCH_SIGNER_CHART_DATA,
            payload: { chartId: getChartId, page: page, limit: limit, collabSecret: chartId },
          });
          // NOVA-148: signing the LAST chart no longer redirects — the
          // refetch flips the session to all-signed and the attestation step
          // (below) takes over; accepting it completes the packet and
          // performs the portal redirect.
          // NOVA-176: EXCEPT for pre-attestation-era packets
          // (requires_attestation = 0): the backend completes those on this
          // final submit, so finish the session the pre-NOVA-148 way and
          // return the physician to the portal (which restores their month).
          if (
            packet?.requires_attestation === 0 &&
            data?.statusCounts?.totalUncompleted === 1
          ) {
            toast.success("Thank you for signing the documents");
            returnToChartList();
          }
        }
      } catch (err) {
        // NOVA-138: this catch used to swallow every upload/submit failure,
        // leaving the Sign button a silent no-op. Surface the backend's
        // message (it sends useful ones: rate limit, file too large, auth).
        showErrorToast(
          err?.response?.data?.message ||
            "Failed to submit the signed chart. Please try again."
        );
        setIsLoading(false);
      }
    },
    [
      currentDoc,
      data?.statusCounts?.totalUncompleted,
      dispatch,
      getChartId,
      limit,
      page,
      signerInfo?.id,
      packet?.requires_attestation,
      returnToChartList,
    ]
  );

  const generateModifiedPdf = async () => {
    if (!currentPdf || !signImage || filteredCoordinates.length === 0) {
      return false;
    }

    try {
      const pdfResponse = await fetch(currentPdf);
      const pdfArrayBuffer = await pdfResponse.arrayBuffer();
      const existingPdf = await PDFDocument.load(pdfArrayBuffer);

      const imageResponse = await fetch(`${signImage}?t=${Date.now()}`, {
        mode: "cors",
        cache: "no-store",
      });
      const imageArrayBuffer = await imageResponse.arrayBuffer();
      const contentType = imageResponse.headers.get("Content-Type");

      let image;
      if (contentType === "image/png") {
        image = await existingPdf.embedPng(imageArrayBuffer);
      } else if (contentType === "image/jpeg") {
        image = await existingPdf.embedJpg(imageArrayBuffer);
      } else {
        throw new Error("Unsupported image type");
      }

      const pages = existingPdf.getPages();

      filteredCoordinates.forEach(({ pageNumber, x, y }) => {
        const pageIndex = pageNumber - 1;
        if (pageIndex >= 0 && pageIndex < pages.length) {
          const page = pages[pageIndex];
          const { width } = page.getSize();
          const signHeight = 60;
          const signWidth = 200;

          let adjustedY = Math.max(10, y - signHeight);

          page.drawImage(image, {
            x: width - signWidth - 60,
            y: adjustedY + 10,
            width: signWidth,
            height: signHeight,
          });

          page.drawText(formattedSignedDate, {
            x: width - signWidth - 50,
            y: adjustedY,
            size: 9,
            color: rgb(0, 0, 0),
          });
        }
      });

      const modifiedPdf = await existingPdf.save();
      const blob = new Blob([modifiedPdf], { type: "application/pdf" });

      await uploadGeneratedPdf(blob);

      return true;
    } catch (error) {
      console.error("Error adding signature and date:", error);
      showErrorToast(error);
      return false;
    }
  };

  // NOVA-148: packet identity + attestation state for the attestation step.
  const fetchPacket = useCallback(async () => {
    if (!getChartId) return;
    try {
      const response = await axios.get(
        `${serverBaseUrl}/get_collab_chart_info?chart_id=${getChartId}`,
        { headers: { "sign-secret-id": chartId } }
      );
      if (response?.data?.chart) setPacket(response.data.chart);
    } catch (err) {
      // Non-fatal: the attestation panel shows a fallback identity line.
      console.error("Failed to fetch packet info:", err);
    }
  }, [getChartId, chartId]);

  useEffect(() => {
    fetchPacket();
  }, [fetchPacket]);

  // NOVA-148: attestation accept — completes the packet (the server records
  // the packet-level attested_at) and returns the physician to the portal's
  // chart list, which restores the month they came from.

  const acceptAttestation = async () => {
    setAttesting(true);
    // NOVA-146: attestation locks the notes — flush any unsaved edits FIRST
    // so the last keystrokes make it into the QI record.
    try {
      const dirty = Object.entries(notesRef.current).filter(
        ([, e]) => e.text !== e.savedText
      );
      await Promise.all(dirty.map(([linkId]) => saveNote(Number(linkId))));
    } catch (_) {
      /* saveNote records per-note errors; attestation proceeds regardless */
    }
    try {
      const response = await axios.post(
        `${serverBaseUrl}/attest_collab_chart`,
        { chart_id: getChartId },
        { headers: { "sign-secret-id": chartId } }
      );
      if (response?.data?.success) {
        toast.success("Packet attested and submitted — thank you");
        // Return to the month the review was opened from. NOVA-182: the portal
        // now derives the open folder from ?month= (not sessionStorage), so the
        // month must be on the URL — same as returnToChartList's Back control.
        // Go to /app (not the portal ROOT, which bounces through login before
        // its auth check settles).
        if (frontendCollabUrl) {
          const base = `${frontendCollabUrl.replace(/\/+$/, "")}/app`;
          window.location.href = packet?.review_month
            ? `${base}?month=${encodeURIComponent(packet.review_month)}`
            : base;
        } else {
          window.location.reload();
        }
        return;
      }
      showErrorToast(response?.data?.message || "Failed to submit attestation");
      setAttesting(false);
    } catch (err) {
      showErrorToast(
        err?.response?.data?.message || "Failed to submit attestation"
      );
      setAttesting(false);
    }
  };

  // Stamp the adopted signature into the current chart and submit it. The
  // subsequent refetch re-pins the view to the next unsigned chart, so a
  // successful sign IS the advance; on the last chart the upload handler
  // redirects back to the portal instead.
  // NOVA-180: review-only completion — no canvas render, no PDF upload. The
  // backend stamps date_signed server-side and the NOVA-138 refetch effect
  // advances to the next unsigned document.
  const markReviewedAndAdvance = async () => {
    if (!currentDoc?.id) return;
    setIsLoading(true);
    try {
      // sign_link_id is a cross-check only — the server binds the write to
      // the document its secret authenticates, derives the signer from the
      // chart, and stamps the review time server-side.
      await axios.post(
        `${serverBaseUrl}/review_collab_document`,
        {
          sign_link_id: currentDoc.id,
          chart_id: getChartId,
        },
        { headers: { "sign-secret-id": currentDoc.sign_secret_id } }
      );
      dispatch({
        type: FETCH_SIGNER_CHART_DATA,
        payload: { chartId: getChartId, page: page, limit: limit, collabSecret: chartId },
      });
    } catch (err) {
      showErrorToast(
        err?.response?.data?.message ||
          "Failed to mark the chart reviewed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const signAndAdvance = async () => {
    try {
      const isSuccess = await generateModifiedPdf();
      if (!isSuccess) toast.error("Error generating PDF");
      setIsLoading(false);
    } catch (err) {
      console.error("Error in generating PDF:", err);
      showErrorToast("Error generating PDF. Please fix before proceeding.");
      setIsLoading(false);
    }
  };

  const advance = () => {
    if (currentIndex < (sortedPdf?.length || 0) - 1) {
      setCurrentIndex((prevIndex) => prevIndex + 1);
    }
  };

  const prevPDF = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prevIndex) => prevIndex - 1);
    }
  };

  // The single top-right action. Signed/finalized charts just move forward;
  // an unsigned chart signs-and-advances, detouring through the adopt modal
  // the first time (pendingSignAdvance resumes the signing afterwards).
  const handlePrimaryAction = () => {
    if (allFinalized || currentSigned) {
      advance();
      return;
    }
    if (reviewOnly) {
      // NOVA-180: charts are reviewed, not signed. The ONE signature belongs
      // to the packet: "Sign & Complete" on the last chart ensures it exists
      // (adopt modal if the physician has none on file); the attestation that
      // follows is the packet-level sign-off it backs.
      if (isLastChart && !signImage) {
        pendingSignAdvance.current = true;
        onModalClick();
        return;
      }
      markReviewedAndAdvance();
      return;
    }
    if (!signImage) {
      pendingSignAdvance.current = true;
      onModalClick();
      return;
    }
    signAndAdvance();
  };

  useEffect(() => {
    if (pendingSignAdvance.current && signImage && !open) {
      pendingSignAdvance.current = false;
      if (reviewOnly) {
        markReviewedAndAdvance();
      } else {
        signAndAdvance();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signImage, open]);

  const filterCoordinatesByText = (textContent, searchText) => {
    return textContent.flatMap((pageData, count = 0) =>
      pageData.text
        .filter((item) =>
          item.text.toLowerCase().includes(searchText.toLowerCase())
        )
        .map((item) => ({
          id: count++,
          pageNumber: pageData.pageNumber,
          text: item.text,
          x: item.x,
          y: item.y,
        }))
    );
  };

  const getAdminSignature = useCallback(async () => {
    if (signerInfo?.id) {
      try {
        const response = await axios.get(
          `${serverBaseUrl}/get_signature?signerId=${signerInfo?.id}`,
          {
            headers: {
              "sign-secret-id": currentDoc?.sign_secret_id,
            },
          }
        );

        if (response?.data?.data[0]?.url) {
          setSignImage(response?.data?.data[0]?.url);
        } else {
          setSignImage(null);
        }
      } catch (error) {
        setSignImage(null);
      }
    }
  }, [currentDoc, signerInfo?.id]);

  const onSignatureSubmit = async (newImage) => {
    setSignImage(newImage);
    await getAdminSignature();
    setIsLoading(false);
    onClose();
  };

  const uploadSignatureFile = useCallback(
    async (file) => {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("file", file);
      try {
        const uploadResponse = await axios.post(
          `${serverBaseUrl}/upload`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              "sign-secret-id": currentDoc?.sign_secret_id,
            },
          }
        );
        if (uploadResponse.data?.data?.url) {
          if (signImage != null) {
            const response = await axios.put(
              `${serverBaseUrl}/update_signature`,
              {
                signerId: signerInfo?.id,
                clinicId: currentDoc?.qualiphy_clinic_id || 0,
                url: uploadResponse.data?.data?.url,
                path: uploadResponse.data?.data?.url.split("/").pop(),
              },
              {
                headers: {
                  "sign-secret-id": currentDoc?.sign_secret_id,
                },
              }
            );
            if (response?.data?.success) {
              onSignatureSubmit(uploadResponse.data?.data?.url);
            } else {
              showErrorToast(
                "Something went wrong while uploading signature, please try again"
              );
              setIsLoading(false);
            }
            return;
          }
          const response = await axios.post(
            `${serverBaseUrl}/create_signature`,
            {
              signerId: signerInfo?.id,
              clinicId: currentDoc?.qualiphy_clinic_id,
              url: uploadResponse.data?.data?.url,
              path: uploadResponse.data?.data?.url.split("/").pop(),
            },
            {
              headers: {
                "sign-secret-id": currentDoc?.sign_secret_id,
              },
            }
          );
          if (response?.data?.success) {
            onSignatureSubmit(uploadResponse.data?.data?.url);
          } else {
            showErrorToast(
              "Something went wrong while uploading signature, please try again"
            );
            setIsLoading(false);
          }
        }
      } catch (err) {
        setIsLoading(false);
      }
    },
    [currentDoc, signerInfo?.id, signImage]
  );

  useEffect(() => {
    let isMounted = true;
    let loadingTask;

    // Reset stale coordinates immediately: the primary action is gated on
    // them, and stamping chart N with chart N-1's coordinates must never
    // happen while the new extraction is in flight.
    setTextContent([]);
    setFilteredCoordinates([]);

    // Coordinates are only needed to stamp a signature, so skip the (worker-
    // heavy) extraction for already-signed and finalized charts.
    if (!currentPdf || currentSigned || allFinalized) return undefined;

    const extractTextWithCoordinates = async () => {
      try {
        loadingTask = pdfjs.getDocument({ url: currentPdf });
        const pdfDocument = await loadingTask.promise;
        const numPages = pdfDocument.numPages;
        const allTextContent = [];

        for (let pageNumber = 1; pageNumber <= numPages; pageNumber++) {
          const page = await pdfDocument.getPage(pageNumber);
          const textContent = await page.getTextContent();

          const pageText = textContent.items.map((item) => ({
            text: item.str,
            x: item.transform[4],
            y: item.transform[5],
          }));

          allTextContent.push({ pageNumber, text: pageText });
        }

        if (isMounted) setTextContent(allTextContent);
      } catch (err) {
        console.error("Error extracting text:", err);
      }
    };

    extractTextWithCoordinates();

    return () => {
      isMounted = false;
      // Cancel any in-flight parse so a remount doesn't leave a task pending on
      // the shared pdfjs worker (the source of the "Worker was terminated"
      // rejection when react-pdf's <Document> tears the worker down).
      // destroy() returns a promise that itself rejects if the worker is
      // already gone — catch it or it becomes another unhandled rejection.
      try {
        loadingTask?.destroy()?.catch?.(() => {});
      } catch (_) {
        /* already settled */
      }
    };
  }, [currentPdf, currentSigned, allFinalized]);

  useEffect(() => {
    if (searchText && textContent.length > 0) {
      setFilteredCoordinates(filterCoordinatesByText(textContent, searchText));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, textContent]);

  useEffect(() => {
    getAdminSignature();
  }, [getAdminSignature]);

  // NOVA-148: the attestation step appears once every chart in the packet is
  // signed and the packet has not been attested yet (AC1). Previous/Next stay
  // live so the physician can re-check any chart before accepting.
  // NOVA-176: packets stamped requires_attestation = 0 predate the feature —
  // their charts were reviewed under the old flow, so no retroactive
  // attestation is demanded (viewing a completed legacy packet shows no
  // panel, and finishing a legacy packet completes on the last signature).
  // A missing field (older collab-backend) keeps the attestation, so the
  // frontend can deploy before the backend without behavior change.
  const needsAttestation =
    allFinalized && packet && !packet.attested_at && packet.requires_attestation !== 0;
  // NOVA-146: notes are editable — on any chart, signed or not, across
  // Previous/Next and across full exits — until the packet is attested.
  const notesLocked = !!packet?.attested_at;
  const packetLabel = packet
    ? `${packet.operator_name || "All providers"} — ${packet.review_month}`
    : "";
  const reviewedOn =
    currentSigned && currentDoc?.date_signed
      ? moment(currentDoc.date_signed).format("MM/DD/YYYY")
      : null;

  const browsing = allFinalized || currentSigned;
  // NOVA-180: attributed packets use review language — the per-chart action
  // no longer signs anything. Legacy packets keep the signing labels/flow.
  const primaryLabel = browsing
    ? "Next"
    : reviewOnly
    ? isLastChart
      ? "Sign & Complete"
      : "Reviewed + Next"
    : isLastChart
    ? "Sign + Finish"
    : "Sign + Next";
  const primaryDisabled = browsing
    ? currentIndex >= (sortedPdf?.length || 1) - 1
    : reviewOnly
    ? !pdfReady || isLoading
    : !pdfReady || filteredCoordinates.length === 0 || isLoading;

  return (
    <div className="p-4 w-full mb-4">
      {/* Always-available exit from the review, back to the month it was
          opened from. Sits above the chart controls so it never competes with
          Previous (which moves between charts, not out of the packet).
          NOVA-129: the exit is labelled by destination and paired with a
          "Progress autosaved" note, because there is no explicit save step —
          every signature/review and note is persisted server-side as it
          happens (Michael, #NOVA-129), so leaving is always safe. */}
      <div className="flex items-center justify-between gap-3 mb-2">
        <button
          type="button"
          id="back-to-chart-list"
          data-testid="back-to-chart-list"
          onClick={returnToChartList}
          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
        >
          <span aria-hidden="true">←</span>
          <span>
            Back to {packet?.review_month ? packet.review_month : "chart list"}
          </span>
        </button>
        <span
          data-testid="autosave-note"
          className="inline-flex items-center gap-1 text-xs text-gray-500"
        >
          <span aria-hidden="true">✓</span>
          Progress autosaved
        </span>
      </div>
      <div className="flex items-center justify-between gap-3 mb-3">
        <CustomButton
          id="previous"
          label="Previous"
          onClick={prevPDF}
          labelColor="black"
          disabled={currentIndex === 0 || isLoading}
        />
        <h3 className="font-semibold text-center">
          {allFinalized ? "All Charts Reviewed" : "Chart Review"}{" "}
          {totalResults > 0 ? `${currentIndex + 1} of ${totalResults}` : ""}
          {reviewedOn && (
            <span className="block text-xs font-normal text-gray-500">
              Reviewed on {reviewedOn}
            </span>
          )}
        </h3>
        <CustomButton
          id="submit"
          label={primaryLabel}
          onClick={handlePrimaryAction}
          labelColor="black"
          isLoading={isLoading}
          disabled={primaryDisabled}
        />
      </div>

      {/* NOVA-129: packet progress bar. The "N of M" text above is the CP's
          POSITION; this bar is REVIEWED/total, which is the compliance-relevant
          figure (how much of the packet is done, not where the cursor sits).
          They differ whenever the CP pages backwards over already-signed
          charts. Only shown once we know the total, so it never renders a bar
          against an unknown denominator. */}
      {totalResults > 0 && (
        <div className="mb-3" data-testid="packet-progress">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>
              {reviewedCount} of {totalResults} reviewed
            </span>
            <span data-testid="packet-progress-pct">{progressPct}%</span>
          </div>
          <div
            className="h-1.5 rounded bg-gray-200 overflow-hidden"
            role="progressbar"
            aria-label="Packet review progress"
            aria-valuenow={progressPct}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className="h-full rounded bg-primary transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      )}

      {error && <p style={{ color: "red" }}>{error}</p>}

      {needsAttestation && (
        <div
          data-testid="attestation-panel"
          className="border-2 border-indigo-200 bg-indigo-50 rounded-lg p-5 mb-3"
        >
          <h3 className="font-semibold text-lg mb-1">Attestation</h3>
          <p className="text-sm text-gray-600 mb-3" data-testid="attestation-packet">
            Chart review packet: <span className="font-semibold">{packetLabel}</span>
          </p>
          <p className="text-sm border rounded bg-white p-3 mb-3" data-testid="attestation-text">
            {ATTESTATION_TEXT}
          </p>
          <label className="flex items-start gap-2 text-sm mb-4 cursor-pointer">
            <input
              type="checkbox"
              data-testid="attestation-checkbox"
              className="mt-0.5"
              checked={attestChecked}
              onChange={(e) => setAttestChecked(e.target.checked)}
            />
            <span>I have read and agree to the attestation above.</span>
          </label>
          <CustomButton
            id="attest-submit"
            label="Accept & Submit Packet"
            onClick={acceptAttestation}
            labelColor="black"
            isLoading={attesting}
            disabled={!attestChecked || attesting}
          />
        </div>
      )}
      {allFinalized && packet?.attested_at && (
        <p className="text-sm text-gray-600 mb-3" data-testid="attested-on">
          Packet attested on {moment(packet.attested_at).format("MM/DD/YYYY")}
          {packetLabel ? ` — ${packetLabel}` : ""}
        </p>
      )}

      {/* NOVA-146: the PDF scrolls inside its frame; the notes panel sits
          beside it and stays put. Stacks below the frame on narrow screens. */}
      <div className="flex flex-col lg:flex-row gap-3 items-stretch">
        <div
          ref={frameRef}
          data-testid="chart-frame"
          className="border border-gray-300 rounded-lg overflow-y-auto bg-gray-100 flex-1 min-w-0"
          style={{ height: "min(75vh, 1000px)", minHeight: "400px" }}
        >
          {!pdfReady && !error && <p className="p-4">Loading PDF...</p>}
          {pdfReady && (
            <div className="w-fit mx-auto py-4">
              <Document
                key={currentIndex}
                file={currentPdf}
                onLoadSuccess={onDocumentLoadSuccess}
              >
                {numPages &&
                  Array.from({ length: numPages }, (_, index) => (
                    <div key={index} style={{ marginBottom: "20px" }}>
                      <Page pageNumber={index + 1} width={600} />
                    </div>
                  ))}
              </Document>
            </div>
          )}
        </div>

        {/* Chart notes: associated with THIS document, editable until the
            packet attestation, autosaved (debounce + on navigation + on tab
            hide/close). */}
        {currentLinkId && (
          <div
            data-testid="notes-panel"
            className="border border-gray-300 rounded-lg bg-white p-3 flex flex-col lg:w-80 shrink-0"
            style={{ height: "min(75vh, 1000px)", minHeight: "400px" }}
          >
            <div className="flex items-baseline justify-between mb-1">
              <h4 className="font-semibold">Chart notes</h4>
              <span className="text-xs text-gray-500" data-testid="notes-status">
                {notesLocked
                  ? "Read-only (attested)"
                  : currentNote?.saving
                  ? "Saving…"
                  : currentNote?.error
                  ? `Not saved — ${currentNote.error}`
                  : currentNote && currentNote.text !== currentNote.savedText
                  ? "Unsaved changes"
                  : currentNote?.savedAt
                  ? `Saved ${moment(currentNote.savedAt).format("MM/DD h:mm A")}`
                  : "No note yet"}
              </span>
            </div>
            <textarea
              data-testid="notes-input"
              className="flex-1 w-full border rounded p-2 text-sm resize-none disabled:bg-gray-100 disabled:text-gray-600"
              placeholder={
                notesLocked
                  ? "No note was added for this chart."
                  : "Optional notes on this chart — for your QI meetings. Saved automatically."
              }
              value={currentNote?.text || ""}
              disabled={notesLocked}
              onChange={(e) =>
                setNotes((p) => ({
                  ...p,
                  [currentLinkId]: {
                    ...(p[currentLinkId] || { savedText: "", savedAt: null }),
                    text: e.target.value,
                    saving: false,
                    error: null,
                  },
                }))
              }
            />
            {currentNote?.savedAt && (
              <p className="text-[11px] text-gray-400 mt-1" data-testid="notes-timestamps">
                Last edited {moment(currentNote.savedAt).format("MM/DD/YYYY h:mm A")}
              </p>
            )}
          </div>
        )}
      </div>

      <SignatureModal
        isLoading={isLoading}
        defaultName={signerInfo?.name}
        signatureType={""}
        onSubmit={(signedDoc) => {
          uploadSignatureFile(signedDoc);
        }}
        onClose={onClose}
        open={open}
      />
    </div>
  );
}
